import { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  IconButton,
  Box,
  Typography,
  Autocomplete,
  CircularProgress,
  Chip,
  Checkbox,
} from '@mui/material';
import { Close, AccessTime, Videocam } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, addDays } from 'date-fns';
import { useCreateBooking, useAvailability } from '../../hooks/useBookings';
import { useClients } from '../../hooks/useClients';
import { useServices } from '../../hooks/useServices';
import { useGoogleCalendarStatus } from '../../hooks/useGoogleCalendar';

const createBookingSchema = z.object({
  clientId: z.string().min(1, 'Client is required'),
  serviceId: z.string().min(1, 'Service is required'),
  date: z.date({ required_error: 'Date is required' }),
  timeSlot: z.string().min(1, 'Time slot is required'),
  internalNotes: z.string().max(2000).optional(),
});

type CreateBookingFormData = z.infer<typeof createBookingSchema>;

interface CreateBookingModalProps {
  open: boolean;
  onClose: () => void;
  preselectedClientId?: string;
  preselectedServiceId?: string;
  preselectedDate?: Date | null;
}

export default function CreateBookingModal({
  open,
  onClose,
  preselectedClientId,
  preselectedServiceId,
  preselectedDate,
}: CreateBookingModalProps) {
  const [error, setError] = useState<string | null>(null);
  const [addGoogleMeet, setAddGoogleMeet] = useState(false);
  const createBooking = useCreateBooking();

  // Fetch clients and services
  const { data: clientsData } = useClients({ limit: 100 });
  const { data: servicesData } = useServices({ status: 'active', limit: 100 });
  const { data: gcalStatus } = useGoogleCalendarStatus();

  const {
    control,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<CreateBookingFormData>({
    resolver: zodResolver(createBookingSchema),
    defaultValues: {
      clientId: preselectedClientId || '',
      serviceId: preselectedServiceId || '',
      internalNotes: '',
    },
  });

  const selectedServiceId = watch('serviceId');
  const selectedDate = watch('date');

  // Set preselected date when modal opens with a date from calendar
  useEffect(() => {
    if (preselectedDate && open) {
      setValue('date', preselectedDate);
    }
  }, [preselectedDate, open, setValue]);

  // Fetch availability when service and date are selected
  const { data: availabilityData, isLoading: loadingAvailability } = useAvailability(
    selectedServiceId,
    selectedDate ? format(selectedDate, 'yyyy-MM-dd') : ''
  );

  const selectedService = servicesData?.services.find(s => s._id === selectedServiceId);

  const onSubmit = async (data: CreateBookingFormData) => {
    setError(null);

    try {
      await createBooking.mutateAsync({
        clientId: data.clientId,
        serviceId: data.serviceId,
        startTime: data.timeSlot,
        internalNotes: data.internalNotes,
        addGoogleMeet: addGoogleMeet || undefined,
      });

      reset();
      setAddGoogleMeet(false);
      onClose();
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: { message?: string } } } };
      setError(error.response?.data?.error?.message || 'Failed to create booking');
    }
  };

  const handleClose = () => {
    reset();
    setError(null);
    setAddGoogleMeet(false);
    onClose();
  };

  // Filter only available slots
  const availableSlots = availabilityData?.slots.filter(s => s.available) || [];

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">New Booking</Typography>
            <IconButton onClick={handleClose} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>

        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent dividers>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Grid container spacing={3}>
              {/* Client Selection */}
              <Grid item xs={12}>
                <Controller
                  name="clientId"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth error={!!errors.clientId}>
                      <Autocomplete
                        options={clientsData?.clients || []}
                        getOptionLabel={(option) =>
                          typeof option === 'string'
                            ? option
                            : `${option.firstName} ${option.lastName}`
                        }
                        value={clientsData?.clients.find(c => c._id === field.value) || null}
                        onChange={(_, newValue) => {
                          field.onChange(newValue?._id || '');
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Client"
                            required
                            error={!!errors.clientId}
                            helperText={errors.clientId?.message}
                          />
                        )}
                        renderOption={(props, option) => (
                          <li {...props} key={option._id}>
                            <Box>
                              <Typography>{option.firstName} {option.lastName}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {option.contact?.email}
                              </Typography>
                            </Box>
                          </li>
                        )}
                      />
                    </FormControl>
                  )}
                />
              </Grid>

              {/* Service Selection */}
              <Grid item xs={12}>
                <Controller
                  name="serviceId"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth error={!!errors.serviceId}>
                      <InputLabel>Service</InputLabel>
                      <Select {...field} label="Service" required>
                        {servicesData?.services.map((service) => (
                          <MenuItem key={service._id} value={service._id}>
                            <Box display="flex" alignItems="center" gap={1} width="100%">
                              <Box
                                sx={{
                                  width: 12,
                                  height: 12,
                                  borderRadius: '50%',
                                  bgcolor: service.color,
                                }}
                              />
                              <Box flex={1}>
                                <Typography>{service.name}</Typography>
                              </Box>
                              <Typography variant="body2" color="text.secondary">
                                {service.duration} min - {service.formattedPrice}
                              </Typography>
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.serviceId && (
                        <Typography variant="caption" color="error">
                          {errors.serviceId.message}
                        </Typography>
                      )}
                    </FormControl>
                  )}
                />
              </Grid>

              {/* Service Info */}
              {selectedService && (
                <Grid item xs={12}>
                  <Box
                    sx={{
                      p: 2,
                      bgcolor: 'grey.50',
                      borderRadius: 2,
                      display: 'flex',
                      gap: 2,
                    }}
                  >
                    <Chip
                      icon={<AccessTime />}
                      label={`${selectedService.duration} min`}
                      size="small"
                    />
                    <Chip
                      label={selectedService.meetingType.replace('_', ' ')}
                      size="small"
                      sx={{ textTransform: 'capitalize' }}
                    />
                  </Box>
                </Grid>
              )}

              {/* Google Meet option — shown when service is online and Google Calendar is connected */}
              {selectedService &&
                (selectedService.meetingType === 'online' || selectedService.meetingType === 'flexible') &&
                gcalStatus?.connected &&
                gcalStatus?.isActive && (
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={addGoogleMeet}
                        onChange={(e) => setAddGoogleMeet(e.target.checked)}
                        icon={<Videocam sx={{ color: 'text.disabled' }} />}
                        checkedIcon={<Videocam />}
                      />
                    }
                    label="Add Google Meet link"
                    sx={{ ml: 0 }}
                  />
                </Grid>
              )}

              {/* Date Selection */}
              <Grid item xs={12} sm={6}>
                <Controller
                  name="date"
                  control={control}
                  render={({ field }) => (
                    <DatePicker
                      label="Date"
                      value={field.value || null}
                      onChange={(newValue) => {
                        field.onChange(newValue);
                        setValue('timeSlot', '');  // Reset time slot
                      }}
                      minDate={new Date()}
                      maxDate={addDays(new Date(), 60)}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          required: true,
                          error: !!errors.date,
                          helperText: errors.date?.message,
                        },
                      }}
                    />
                  )}
                />
              </Grid>

              {/* Time Slot Selection */}
              <Grid item xs={12} sm={6}>
                <Controller
                  name="timeSlot"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth error={!!errors.timeSlot}>
                      <InputLabel>Time</InputLabel>
                      <Select
                        {...field}
                        label="Time"
                        required
                        disabled={!selectedDate || !selectedServiceId || loadingAvailability}
                      >
                        {loadingAvailability ? (
                          <MenuItem disabled>
                            <CircularProgress size={20} sx={{ mr: 1 }} />
                            Loading availability...
                          </MenuItem>
                        ) : availableSlots.length === 0 ? (
                          <MenuItem disabled>No available times</MenuItem>
                        ) : (
                          availableSlots.map((slot) => (
                            <MenuItem key={slot.start} value={slot.start}>
                              {format(new Date(slot.start), 'h:mm a')} - {format(new Date(slot.end), 'h:mm a')}
                            </MenuItem>
                          ))
                        )}
                      </Select>
                      {errors.timeSlot && (
                        <Typography variant="caption" color="error">
                          {errors.timeSlot.message}
                        </Typography>
                      )}
                    </FormControl>
                  )}
                />
              </Grid>

              {/* Internal Notes */}
              <Grid item xs={12}>
                <Controller
                  name="internalNotes"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Internal Notes"
                      multiline
                      rows={3}
                      fullWidth
                      placeholder="Notes for your reference (not shared with client)"
                      error={!!errors.internalNotes}
                      helperText={errors.internalNotes?.message}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={handleClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Creating...' : 'Create Booking'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </LocalizationProvider>
  );
}
